import { useRef, useEffect } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

const COLUMN_WIDTH = 40; // Wider spacing = fewer columns
const FALL_SPEED = 40; // pixels per second
const CHARACTERS = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

interface Column {
  x: number;
  y: number;
  speed: number;
  chars: string[];
  trailLength: number;
  updateInterval: number;
  framesSinceUpdate: number;
  isStreaming: boolean;
  gapDuration: number;
}

interface CodeRainProps {
  isDarkMode: boolean;
}

interface SpriteConfig {
  color: string;
  shadowColor: string;
  shadowBlur: number;
  isBold: boolean;
}

/** Returns sprite config for the head character (i === 0) */
function getHeadConfig(isDark: boolean): SpriteConfig {
  return isDark
    ? { color: '#FF00FF', shadowColor: '#FF1493', shadowBlur: 6, isBold: true }
    : { color: '#000000', shadowColor: '', shadowBlur: 0, isBold: true };
}

/** Returns sprite config for the first trail character (i === 1) */
function getTrail1Config(isDark: boolean): SpriteConfig {
  return isDark
    ? { color: '#00ff88', shadowColor: '', shadowBlur: 0, isBold: false }
    : { color: '#C026D3', shadowColor: '', shadowBlur: 0, isBold: false };
}

/** Returns sprite config for the second trail character (i === 2) */
function getTrail2Config(isDark: boolean): SpriteConfig {
  return isDark
    ? { color: '#00ff41', shadowColor: '', shadowBlur: 0, isBold: false }
    : { color: '#06B6D4', shadowColor: '', shadowBlur: 0, isBold: false };
}

/** Returns sprite config for fading trail characters (i > 2) */
function getFadeConfig(isDark: boolean, opacity: number): SpriteConfig {
  if (isDark) {
    const greenVal = Math.floor(65 * opacity);
    const cacheOpacity = Math.floor(opacity * 10) / 10;
    return { color: `rgba(0, ${greenVal}, ${Math.floor(greenVal * 0.54)}, ${cacheOpacity})`, shadowColor: '', shadowBlur: 0, isBold: false };
  }
  const alpha = opacity * 0.7;
  return { color: `rgba(192, 38, 211, ${alpha})`, shadowColor: '', shadowBlur: 0, isBold: false };
}

/** Resolves cache key and sprite config for a trail character at position i */
function resolveCharStyle(i: number, isDark: boolean, char: string, trailLength: number): { cacheKey: string; config: SpriteConfig } {
  const mode = isDark ? 'dark' : 'light';
  if (i === 0) return { cacheKey: `head-${mode}-${char}`, config: getHeadConfig(isDark) };
  if (i === 1) return { cacheKey: `trail1-${mode}-${char}`, config: getTrail1Config(isDark) };
  if (i === 2) return { cacheKey: `trail2-${mode}-${char}`, config: getTrail2Config(isDark) };
  const opacity = 1 - ((i - 2) / (trailLength - 2));
  const cacheOpacity = Math.floor(opacity * 10) / 10;
  return { cacheKey: `fade-${mode}-${char}-${cacheOpacity}`, config: getFadeConfig(isDark, opacity) };
}

function createCharSprite(char: string, cfg: SpriteConfig): HTMLCanvasElement {
  const spriteCanvas = document.createElement('canvas');
  spriteCanvas.width = 32;
  spriteCanvas.height = 32;
  const spriteCtx = spriteCanvas.getContext('2d');
  if (!spriteCtx) return spriteCanvas;

  spriteCtx.font = cfg.isBold ? 'bold 16px monospace' : '14px monospace';
  spriteCtx.fillStyle = cfg.color;
  spriteCtx.shadowColor = cfg.shadowColor;
  spriteCtx.shadowBlur = cfg.shadowBlur;
  spriteCtx.textAlign = 'center';
  spriteCtx.textBaseline = 'middle';
  spriteCtx.fillText(char, 16, 16);

  return spriteCanvas;
}

function handleGapPhase(col: Column): boolean {
  if (col.isStreaming) return false;
  col.gapDuration--;
  if (col.gapDuration <= 0) {
    col.isStreaming = true;
    col.y = -col.trailLength * 20;
    col.gapDuration = 60 + Math.floor(Math.random() * 120);
  }
  return true;
}

function updateColumnPosition(col: Column, deltaTime: number, canvasHeight: number) {
  col.framesSinceUpdate++;
  if (col.framesSinceUpdate < col.updateInterval) return;

  col.framesSinceUpdate = 0;
  col.y += (col.speed * deltaTime) / 1000;

  if (col.y > canvasHeight + col.trailLength * 20) {
    col.isStreaming = false;
    col.speed = FALL_SPEED + Math.random() * 30;
    col.trailLength = 10 + Math.floor(Math.random() * 15);
    col.updateInterval = 1 + Math.floor(Math.random() * 3);
  }
}

function drawColumnTrail(
  col: Column,
  ctx: CanvasRenderingContext2D,
  canvasHeight: number,
  isDark: boolean,
  charCache: Map<string, HTMLCanvasElement>
) {
  for (let i = 0; i < col.trailLength; i++) {
    const charY = col.y - i * 20;
    if (charY <= 0 || charY >= canvasHeight) continue;

    const char = CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)];
    const { cacheKey, config } = resolveCharStyle(i, isDark, char, col.trailLength);

    if (!charCache.has(cacheKey)) {
      charCache.set(cacheKey, createCharSprite(char, config));
    }

    const sprite = charCache.get(cacheKey)!;
    ctx.drawImage(sprite, col.x - 16, charY - 16);
  }
}

const CodeRain = ({ isDarkMode }: CodeRainProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  // Use ref to avoid triggering effect re-run when dark mode changes
  const isDarkModeRef = useRef(isDarkMode);

  // Keep ref in sync with prop
  isDarkModeRef.current = isDarkMode;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const columns: Column[] = [];
    const charCache = new Map<string, HTMLCanvasElement>();

    const resizeCanvas = () => {
      const scale = 0.75;
      canvas.width = window.innerWidth * scale;
      canvas.height = window.innerHeight * scale;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    const createColumns = () => {
      columns.length = 0;
      const numColumns = Math.ceil(canvas.width / COLUMN_WIDTH);

      for (let i = 0; i < numColumns; i++) {
        const startStreaming = Math.random() > 0.5;
        columns.push({
          x: i * COLUMN_WIDTH,
          y: startStreaming ? -Math.random() * canvas.height : 0,
          speed: FALL_SPEED + Math.random() * 30,
          chars: [],
          trailLength: 10 + Math.floor(Math.random() * 15),
          updateInterval: 1 + Math.floor(Math.random() * 3),
          framesSinceUpdate: 0,
          isStreaming: startStreaming,
          gapDuration: startStreaming ? 0 : 60 + Math.floor(Math.random() * 120),
        });
      }
    };

    resizeCanvas();
    createColumns();

    const handleResize = () => {
      resizeCanvas();
      createColumns();
    };

    window.addEventListener('resize', handleResize);

    if (prefersReducedMotion) {
      columns.forEach((col) => {
        ctx.font = '14px monospace';
        ctx.fillStyle = isDarkModeRef.current ? '#00ff41' : '#C026D3';
        ctx.fillText(
          CHARACTERS[Math.floor(Math.random() * CHARACTERS.length)],
          col.x,
          canvas.height / 2
        );
      });
      return;
    }

    let lastTime = 0;
    const animate = (time: number) => {
      const deltaTime = time - lastTime;
      lastTime = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      columns.forEach((col) => {
        if (handleGapPhase(col)) return;
        updateColumnPosition(col, deltaTime, canvas.height);
        drawColumnTrail(col, ctx, canvas.height, isDarkModeRef.current, charCache);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [prefersReducedMotion]); // isDarkMode NOT in deps - uses ref to avoid re-initialization

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
      }}
      aria-hidden="true"
    />
  );
};

export default CodeRain;
